import React, { Fragment, useCallback, useEffect, useState } from "react";

import { css } from "@emotion/core";
import { SplitFactory, SplitTreatments } from "@splitsoftware/splitio-react";
import { isFunction } from "lodash";

import { Alert, AlertDescription, Margin, Spacer } from "@zapier/design-system";
import { buildDowngradeWarningUrl } from "@zapier/downgrade";
import {
  useMaybeSendFullStoryEvent,
  useTrackingContext,
} from "@zapier/toolbox-tracking";

import {
  useApolloInjection,
  withApolloInjectionProvider,
} from "@zapier/checkout/esm/contexts/ApolloInjection";
import { CurrencyProvider } from "@zapier/checkout/esm/contexts/CurrencyContext";
import {
  useChangePlan,
  useCurrentAccountId,
  usePaymentIntentsMachine,
} from "@zapier/checkout/esm/hooks";
import {
  ApolloInjection,
  ChangeType,
  Interval,
  InvoiceType,
  PlanV2,
} from "@zapier/checkout/esm/types";
// Styles
import { checkoutWrapperCss } from "@zapier/checkout/esm/utils/styles";
import {
  PaymentApp,
  PendingPaymentWarningBar,
  useCurrencyRuleset,
  usePaymentSource,
  usePendingPayment,
} from "@zapier/payment";

import { usePrevious } from "../../hooks/usePrevious";
import { AsyncOverlay } from "../AsyncOverlay";
import { ChangePlanCompleteModal } from "../ChangePlanCompleteModal";
import { CheckoutSummary } from "../CheckoutSummary";
import {
  PaymentIntentFatalErrorModal,
  RetryPaymentIntentAlert,
} from "../PaymentIntentErrorUi";
import { CheckoutLoading } from "./../CheckoutLoading";
import { CheckoutSection } from "./../CheckoutSection";
import { BillingOptions } from "./BillingOptions";
import { ChangePlanButtons } from "./ChangePlanButtons";
import { ChangePlanCurrencyDropdown } from "./ChangePlanCurrencyDropdown";
import { ChangePlanHeader } from "./ChangePlanHeader";
import { ChangePlanIndicator } from "./ChangePlanIndicator";
import { PlanNotFound } from "./PlanNotFound";
import {
  useCurrentAccount,
  useInvoicePreview,
  usePlans,
  usePromoPlans,
  useSelectedCurrency,
} from "./hooks";
import {
  findPlans,
  getPageText,
  isSamePlanAndInterval,
  unsupportedPaymentSource,
} from "./utils";

const AUTO_UPGRADE_FEATURE_NAME = "AUTO_UPGRADE";

type Props = {
  /** `upgrade`, `downgrade` etc. */
  changeType: ChangeType;
  /** Allows changing of wrapper styles */
  cssOverrides?: { [key: string]: string };
  /** ISO code for selected currency */
  currency?: string;
  /** The interval selected. Should be used when controlled via `onSelectInterval` */
  interval?: Interval;
  /** Hides the header */
  isCompact?: boolean;
  /** Set this to true when the change plan page appears inside an inline upgrade nudge. */
  isEmbeddedInInlineUpgrade?: boolean;
  /** Called when cancel button is clicked */
  onCancel: () => void;
  /** Called when change plan action is successful */
  onFinishChangePlan?: () => void;
  /** Called when interval is clicked. Passing a function here makes this a controlled component */
  onSelectInterval?: (interval: Interval) => void;
  /** Called when currency is selected. Passing a function here makes this a controlled component. Use with currency prop. */
  onSelectCurrency?: (currency: string) => void;
  /** Flag to indicate whether to route to downgrade confirmation page before downgrading */
  showDowngradeConfirmation?: boolean;
  /** Flag to indicate whether to show the change plan success modal or not */
  showSuccessModal: boolean;
  /** The name of the plan changing to */
  toPlanName: string;
  /** Temporary: allows Split.io localhost mode */
  splitLocalhostMode?: boolean;
} & ApolloInjection;

const changePlanCustomCss = (props: Props) =>
  css([
    props.isCompact &&
      css`
        border: none;
      `,
    props.cssOverrides && css(props.cssOverrides),
  ]);

const buttonsCss = css`
  margin-top: 2em;
`;

const errorCss = css`
  margin-top: 18px;
  text-align: center;
`;

const ChangePlanComponentWithPaymentIntents = (props: Props) => {
  const {
    toPlanName,
    changeType,
    currency,
    interval,
    isEmbeddedInInlineUpgrade = false,
    onSelectCurrency,
    onSelectInterval,
    showSuccessModal,
  } = props;

  const { useQuery, useMutation } = useApolloInjection();
  const { trackEvent } = useTrackingContext();
  const { accountId } = useCurrentAccountId({ useQuery });

  const { currentAccount, isLoading: isLoadingCurrentAccount } =
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    useCurrentAccount(accountId!, { skip: !accountId }, useQuery);

  const autoUpgradeEnrollment = (
    currentAccount?.featureEnrollmentList || []
  ).find(e => e?.featureName === AUTO_UPGRADE_FEATURE_NAME);

  const {
    currentPlan,
    plans,
    isLoading: isLoadingPlans,
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  } = usePlans(accountId!, { skip: !accountId }, useQuery);

  const { isLoading: isLoadingPromoPlans, promoPlans } =
    usePromoPlans(useQuery);

  // INTERVAL STATE -------------
  const [selectedInterval, setSelectedInterval] = useState<Interval>("year");
  // Current interval is props.interval if being controlled, or internal state if uncontrolled
  const currentInterval = interval ?? selectedInterval;

  // CURRENCY STATE -------------
  const {
    selectedCurrency,
    setSelectedCurrency,
    isLoading: isLoadingSelectedCurrency,
  } = useSelectedCurrency({
    currency,
    onSelectCurrency,
    useQuery,
  });

  const { forceSourceEdit, primarySource } = usePaymentSource({
    accountId,
    useQuery,
  });

  const { pendingInvoice } = usePendingPayment({ useQuery });

  const { currencyRuleset, isLoading: isLoadingCurrencyRuleset } =
    useCurrencyRuleset({ useQuery });

  const isLoading =
    isLoadingCurrentAccount ||
    isLoadingPlans ||
    isLoadingPromoPlans ||
    isLoadingSelectedCurrency ||
    isLoadingCurrencyRuleset;

  const plansWithPromos = isLoading ? [] : [...plans, ...promoPlans];

  useMaybeSendFullStoryEvent(
    currentAccount?.switches?.trackUpgradesByPlan && changeType === "upgrade",
    `Began Upgrade From Plan ${currentPlan?.id}`
  );

  const [toPlan, toPlanAlt] = findPlans(
    toPlanName,
    plansWithPromos as PlanV2[],
    currentInterval
  );

  // INVOICE PREVIEW -------------
  const {
    error: invoiceError,
    invoice,
    isLoading: isLoadingInvoice,
  } = useInvoicePreview({
    accountId: currentAccount?.id as number,
    planId: toPlan?.id,
    currency: selectedCurrency.name,
    paymentSourceId: primarySource?.id,
    useQuery,
  });

  // CHANGE PLAN -------------
  const { changePlan, changePlanError, hasChangedPlan, isChangingPlan } =
    useChangePlan({
      accountId,
      planId: toPlan?.id,
      downgradeReason: null,
      currency: selectedCurrency.name,
      invoicedAt: invoice?.invoicedAt,
      paymentIntentId: invoice?.paymentIntent?.id,
      useMutation,
    });

  const shouldShowDowngradeConfirmation =
    changeType === "downgrade" && props.showDowngradeConfirmation;

  const onChangePlan = async () => {
    if (shouldShowDowngradeConfirmation) {
      return null;
    }

    await changePlan();

    trackEvent("Billing: plan changed", {
      accountId,
      plan: toPlan?.name,
      planId: toPlan?.id,
      interval,
    });

    try {
      window.localStorage.setItem("billing_upgrade_date", String(Date.now()));
    } catch (e) {
      // eslint-disable-next-line
      console.log("write to localStorage failed", e);
    }

    if (isFunction(props.onFinishChangePlan)) {
      return props.onFinishChangePlan();
    }
    return null;
  };

  const onCancelChange = () => {
    trackEvent("Billing: cancelChangePlan", {
      accountId,
      fromPlanName: currentPlan?.name,
      toPlanName: toPlan?.name,
      interval,
      changeType,
      step: "first step",
    });

    props.onCancel();
  };

  const onSelectIntervalCallback = useCallback(
    (newInterval: Interval) => {
      return isFunction(onSelectInterval)
        ? onSelectInterval(newInterval)
        : setSelectedInterval(newInterval);
    },
    [onSelectInterval]
  );

  // Ensure selected interval and `toPlan.interval` match
  useEffect(() => {
    if (
      !isLoading &&
      plansWithPromos.length > 0 &&
      toPlan &&
      toPlan.interval !== currentInterval
    ) {
      onSelectIntervalCallback(toPlan.interval);
    }
  }, [
    currentInterval,
    isLoading,
    onSelectIntervalCallback,
    plansWithPromos,
    toPlan,
  ]);

  const hasNoPaymentSource =
    toPlan && toPlan.amount !== 0 && (!primarySource || forceSourceEdit);

  const hasUnsupportedPaymentSource = unsupportedPaymentSource(
    currencyRuleset,
    primarySource,
    selectedCurrency
  );

  const { heading, subheading, submitText } = getPageText(changeType, toPlan);
  const popoverText = hasNoPaymentSource
    ? "Please add a payment method."
    : pendingInvoice
    ? "Cannot change plan with a pending payment."
    : hasUnsupportedPaymentSource
    ? "Please use a supported payment method."
    : null;

  const {
    isLoading: isLoadingPaymentIntents,
    canSubmit,
    hasFatalError,
    hasPaymentConfirmationError,
    submit,
    retryPayment,
  } = usePaymentIntentsMachine(invoice?.paymentIntent, onChangePlan, useQuery);

  const isSubmitDisabled =
    isLoadingInvoice ||
    hasNoPaymentSource ||
    Boolean(pendingInvoice) ||
    hasUnsupportedPaymentSource ||
    isSamePlanAndInterval(currentPlan, toPlan) ||
    !canSubmit;

  if (isLoading) {
    return <CheckoutLoading />;
  }

  if (!toPlan) {
    return <PlanNotFound toPlanName={toPlanName} />;
  }

  return (
    <CurrencyProvider selectedCurrency={selectedCurrency}>
      <Fragment>
        {showSuccessModal && hasChangedPlan && (
          <ChangePlanCompleteModal
            accountId={accountId}
            changeType={changeType}
            newPlan={toPlan as PlanV2}
          />
        )}
        <AsyncOverlay
          isVisible={isLoadingPaymentIntents || isChangingPlan}
          renderAsyncIndicator={() => <ChangePlanIndicator />}
        >
          <section css={[checkoutWrapperCss, changePlanCustomCss(props)]}>
            {!props.isCompact && (
              <ChangePlanHeader
                accountName={
                  currentAccount?.owner?.email ?? currentAccount?.name
                }
                companyName={currentAccount?.companyName}
                heading={heading}
                isEmbeddedInInlineUpgrade={isEmbeddedInInlineUpgrade}
                subheading={subheading}
              />
            )}

            <ChangePlanCurrencyDropdown
              onSelectCurrency={setSelectedCurrency}
              selectedCurrency={selectedCurrency}
            />

            <Spacer height={20} />

            {pendingInvoice && (
              <Margin margin="0 0 10px 0">
                <PendingPaymentWarningBar pendingInvoice={pendingInvoice} />
              </Margin>
            )}

            <BillingOptions
              isAutoUpgradeEnrolled={autoUpgradeEnrollment?.isEnrolled}
              onSelectOption={onSelectIntervalCallback}
              planOptions={([toPlan, toPlanAlt] as PlanV2[])
                .sort((a, b) => b.amount - a.amount)
                .filter(p => p !== undefined)}
              selectedOption={currentInterval}
            />

            {toPlan && toPlan.amount !== 0 && (
              <CheckoutSection
                {...(!hasNoPaymentSource
                  ? { title: "Payment information" }
                  : {})}
              >
                <PaymentApp
                  editInModal={true}
                  selectedCurrency={selectedCurrency}
                  useMutation={useMutation}
                  useQuery={useQuery}
                />
              </CheckoutSection>
            )}

            <CheckoutSection title="Summary">
              {toPlan && (
                <CheckoutSummary
                  error={invoiceError}
                  invoice={invoice}
                  isLoading={isLoadingInvoice}
                  plan={toPlan}
                  useQuery={useQuery}
                  invoiceType={InvoiceType.preview}
                />
              )}
            </CheckoutSection>

            <div css={buttonsCss}>
              <ChangePlanButtons
                downgradeHref={
                  shouldShowDowngradeConfirmation
                    ? buildDowngradeWarningUrl(
                        currentPlan?.name ?? "",
                        toPlanName,
                        currentInterval,
                        selectedCurrency.name
                      )
                    : undefined
                }
                isDisabled={isSubmitDisabled}
                onCancelChangePlan={onCancelChange}
                onChangePlan={submit}
                popoverText={popoverText}
                submitText={submitText}
              />
            </div>

            {changePlanError && (
              <div css={errorCss}>
                <Alert type="error">
                  <AlertDescription>{changePlanError}</AlertDescription>
                </Alert>
              </div>
            )}

            {hasFatalError ? (
              <PaymentIntentFatalErrorModal />
            ) : hasPaymentConfirmationError ? (
              <RetryPaymentIntentAlert retry={retryPayment} />
            ) : null}
          </section>
        </AsyncOverlay>
      </Fragment>
    </CurrencyProvider>
  );
};

export const ChangePlanComponent = (props: Props) => {
  const {
    toPlanName,
    changeType,
    currency,
    interval,
    isEmbeddedInInlineUpgrade = false,
    onSelectCurrency,
    onSelectInterval,
    showSuccessModal,
  } = props;

  const { useQuery, useMutation } = useApolloInjection();
  const { trackEvent } = useTrackingContext();
  const { accountId } = useCurrentAccountId({ useQuery });

  const { currentAccount, isLoading: isLoadingCurrentAccount } =
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    useCurrentAccount(accountId!, { skip: !accountId }, useQuery);

  const autoUpgradeEnrollment = (
    currentAccount?.featureEnrollmentList || []
  ).find(e => e?.featureName === AUTO_UPGRADE_FEATURE_NAME);

  const {
    currentPlan,
    plans,
    isLoading: isLoadingPlans,
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  } = usePlans(accountId!, { skip: !accountId }, useQuery);

  const { isLoading: isLoadingPromoPlans, promoPlans } =
    usePromoPlans(useQuery);

  // INTERVAL STATE -------------
  const [selectedInterval, setSelectedInterval] = useState<Interval>("year");
  // Current interval is props.interval if being controlled, or internal state if uncontrolled
  const currentInterval = interval ?? selectedInterval;

  // CURRENCY STATE -------------
  const {
    selectedCurrency,
    setSelectedCurrency,
    isLoading: isLoadingSelectedCurrency,
  } = useSelectedCurrency({
    currency,
    onSelectCurrency,
    useQuery,
  });

  const { forceSourceEdit, primarySource } = usePaymentSource({
    accountId,
    useQuery,
  });

  const { pendingInvoice } = usePendingPayment({ useQuery });

  const { currencyRuleset, isLoading: isLoadingCurrencyRuleset } =
    useCurrencyRuleset({ useQuery });

  const isLoading =
    isLoadingCurrentAccount ||
    isLoadingPlans ||
    isLoadingPromoPlans ||
    isLoadingSelectedCurrency ||
    isLoadingCurrencyRuleset;

  const plansWithPromos = isLoading ? [] : [...plans, ...promoPlans];

  useMaybeSendFullStoryEvent(
    currentAccount?.switches?.trackUpgradesByPlan && changeType === "upgrade",
    `Began Upgrade From Plan ${currentPlan?.id}`
  );

  const [toPlan, toPlanAlt] = findPlans(
    toPlanName,
    plansWithPromos as PlanV2[],
    currentInterval
  );

  // INVOICE PREVIEW -------------
  const {
    error: invoiceError,
    invoice,
    isLoading: isLoadingInvoice,
  } = useInvoicePreview({
    accountId: currentAccount?.id as number,
    planId: toPlan?.id,
    currency: selectedCurrency.name,
    paymentSourceId: primarySource?.id,
    useQuery,
  });

  // CHANGE PLAN -------------
  const { changePlan, changePlanError, hasChangedPlan, isChangingPlan } =
    useChangePlan({
      accountId,
      planId: toPlan?.id,
      downgradeReason: null,
      currency: selectedCurrency.name,
      invoicedAt: invoice?.invoicedAt,
      useMutation,
    });

  const shouldShowDowngradeConfirmation =
    changeType === "downgrade" && props.showDowngradeConfirmation;

  const onChangePlan = async () => {
    if (shouldShowDowngradeConfirmation) {
      return null;
    }

    await changePlan();

    trackEvent("Billing: plan changed", {
      accountId,
      plan: toPlan?.name,
      planId: toPlan?.id,
      interval,
    });

    try {
      window.localStorage.setItem("billing_upgrade_date", String(Date.now()));
    } catch (e) {
      // eslint-disable-next-line
      console.log("write to localStorage failed", e);
    }

    if (isFunction(props.onFinishChangePlan)) {
      return props.onFinishChangePlan();
    }
    return null;
  };

  const onCancelChange = () => {
    trackEvent("Billing: cancelChangePlan", {
      accountId,
      fromPlanName: currentPlan?.name,
      toPlanName: toPlan?.name,
      interval,
      changeType,
      step: "first step",
    });

    props.onCancel();
  };

  const onSelectIntervalCallback = useCallback(
    (newInterval: Interval) => {
      return isFunction(onSelectInterval)
        ? onSelectInterval(newInterval)
        : setSelectedInterval(newInterval);
    },
    [onSelectInterval]
  );

  // Ensure selected interval and `toPlan.interval` match
  useEffect(() => {
    if (
      !isLoading &&
      plansWithPromos.length > 0 &&
      toPlan &&
      toPlan.interval !== currentInterval
    ) {
      onSelectIntervalCallback(toPlan.interval);
    }
  }, [
    currentInterval,
    isLoading,
    onSelectIntervalCallback,
    plansWithPromos,
    toPlan,
  ]);

  const hasNoPaymentSource =
    toPlan && toPlan.amount !== 0 && (!primarySource || forceSourceEdit);

  const hasUnsupportedPaymentSource = unsupportedPaymentSource(
    currencyRuleset,
    primarySource,
    selectedCurrency
  );

  const isSubmitDisabled =
    isLoadingInvoice ||
    hasNoPaymentSource ||
    Boolean(pendingInvoice) ||
    hasUnsupportedPaymentSource ||
    isSamePlanAndInterval(currentPlan, toPlan);

  const { heading, subheading, submitText } = getPageText(changeType, toPlan);
  const popoverText = hasNoPaymentSource
    ? "Please add a payment method."
    : pendingInvoice
    ? "Cannot change plan with a pending payment."
    : hasUnsupportedPaymentSource
    ? "Please use a supported payment method."
    : null;

  if (isLoading) {
    return <CheckoutLoading />;
  }

  if (!toPlan) {
    return <PlanNotFound toPlanName={toPlanName} />;
  }

  return (
    <CurrencyProvider selectedCurrency={selectedCurrency}>
      <Fragment>
        {showSuccessModal && hasChangedPlan && (
          <ChangePlanCompleteModal
            accountId={accountId}
            changeType={changeType}
            newPlan={toPlan as PlanV2}
          />
        )}
        <AsyncOverlay
          isVisible={isChangingPlan}
          renderAsyncIndicator={() => <ChangePlanIndicator />}
        >
          <section css={[checkoutWrapperCss, changePlanCustomCss(props)]}>
            {!props.isCompact && (
              <ChangePlanHeader
                accountName={
                  currentAccount?.owner?.email ?? currentAccount?.name
                }
                companyName={currentAccount?.companyName}
                heading={heading}
                isEmbeddedInInlineUpgrade={isEmbeddedInInlineUpgrade}
                subheading={subheading}
              />
            )}

            <ChangePlanCurrencyDropdown
              onSelectCurrency={setSelectedCurrency}
              selectedCurrency={selectedCurrency}
            />

            <Spacer height={20} />

            {pendingInvoice && (
              <Margin margin="0 0 10px 0">
                <PendingPaymentWarningBar pendingInvoice={pendingInvoice} />
              </Margin>
            )}

            <BillingOptions
              isAutoUpgradeEnrolled={autoUpgradeEnrollment?.isEnrolled}
              onSelectOption={onSelectIntervalCallback}
              planOptions={([toPlan, toPlanAlt] as PlanV2[])
                .sort((a, b) => b.amount - a.amount)
                .filter(p => p !== undefined)}
              selectedOption={currentInterval}
            />

            {toPlan && toPlan.amount !== 0 && (
              <CheckoutSection
                {...(!hasNoPaymentSource
                  ? { title: "Payment information" }
                  : {})}
              >
                <PaymentApp
                  editInModal={true}
                  selectedCurrency={selectedCurrency}
                  useMutation={useMutation}
                  useQuery={useQuery}
                />
              </CheckoutSection>
            )}

            <CheckoutSection title="Summary">
              {toPlan && (
                <CheckoutSummary
                  error={invoiceError}
                  invoice={invoice}
                  isLoading={isLoadingInvoice}
                  plan={toPlan}
                  useQuery={useQuery}
                  invoiceType={InvoiceType.preview}
                />
              )}
            </CheckoutSection>

            <div css={buttonsCss}>
              <ChangePlanButtons
                downgradeHref={
                  shouldShowDowngradeConfirmation
                    ? buildDowngradeWarningUrl(
                        currentPlan?.name ?? "",
                        toPlanName,
                        currentInterval,
                        selectedCurrency.name
                      )
                    : undefined
                }
                isDisabled={isSubmitDisabled}
                onCancelChangePlan={onCancelChange}
                onChangePlan={onChangePlan}
                popoverText={popoverText}
                submitText={submitText}
              />
            </div>

            {changePlanError && (
              <div css={errorCss}>
                <Alert type="error">
                  <AlertDescription>{changePlanError}</AlertDescription>
                </Alert>
              </div>
            )}
          </section>
        </AsyncOverlay>
      </Fragment>
    </CurrencyProvider>
  );
};

/**
 * Temporary hook to extract whether a customer's plan has metered billing.
 * This is just to disable payment intents for metered billing until we
 * fix the problem with payment intents and pro-ration.
 */
function usePlanHasMeteredBilling(
  currentAccount: ReturnType<typeof useCurrentAccount>["currentAccount"]
) {
  const previousAccount = usePrevious(currentAccount);
  const [customerHasMeteredBilling, setCustomerHasMeteredBilling] =
    useState(false);

  useEffect(() => {
    /**
     * Capture whether the plan has metered billing when the account first loads.
     * We have to capture the value the moment the account loads because when
     * the account *changes*, the value of whether the plan has metered billing
     * changes.
     */
    if (!previousAccount && currentAccount) {
      setCustomerHasMeteredBilling(currentAccount?.planV2.hasMeteredBilling);
    }
  }, [currentAccount]);

  return customerHasMeteredBilling;
}

const SplitChangePlan = (props: Props) => {
  const { useQuery } = useApolloInjection();
  const { accountId } = useCurrentAccountId({ useQuery });
  const { currentAccount } = useCurrentAccount(
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    accountId!,
    { skip: !accountId },
    useQuery
  );

  const customerHasMeteredBilling = usePlanHasMeteredBilling(currentAccount);

  // These are temporary and will be removed after full rollout out SetupIntents/PaymentIntents
  const isProduction =
    typeof window !== "undefined" &&
    window.location.hostname.includes("zapier.com");

  const authorizationKey = isProduction
    ? "mctu4q1tvfa8hgn8kgt5doe0ev6h68th135"
    : "tra7b4qvk62o373ivf9gc24547fp5cqdv6u0";

  if (!accountId) return null;

  return (
    <SplitFactory
      config={{
        core: {
          authorizationKey: props.splitLocalhostMode
            ? "localhost"
            : authorizationKey,
          key: String(accountId),
        },
        ...((props.splitLocalhostMode ?? false) && {
          features: { billing_fe_payment_intents: "on" },
        }),
      }}
    >
      <SplitTreatments
        attributes={{ email: currentAccount?.owner?.email }}
        names={["billing_fe_payment_intents"]}
      >
        {({ treatments, isReady }) => {
          return isReady &&
            treatments.billing_fe_payment_intents.treatment === "on" &&
            /** Temporarily disable for downgrades until we can re-order the screens and make checkout the last page in the flow */
            props.changeType !== "downgrade" &&
            /** Temporarily disable for plans with metered billing until we can figure out how to include metered costs in the payment intent */
            !customerHasMeteredBilling ? (
            <ChangePlanComponentWithPaymentIntents {...props} />
          ) : (
            <ChangePlanComponent {...props} />
          );
        }}
      </SplitTreatments>
    </SplitFactory>
  );
};

export const ChangePlan = withApolloInjectionProvider<Props>(SplitChangePlan);
